/**
 * Drip JavaScript SDK Service
 * Handles user tagging via Drip's JavaScript SDK through Google Tag Manager
 */

// Extend window object to include Drip's _dcq
declare global {
  interface Window {
    /** Drip's command queue — heterogeneous `[command, payload]` tuples. */
    _dcq: unknown[];
  }
}

interface DripResponse {
  success: boolean;
  message?: string;
}

interface PluginContextData {
  videosTotal?: string;
  onboardingStatus?: string;
  micPermission?: string;
  cameraPermission?: string;
  isPinned?: string;
  source?: string;
}

class DripService {
  private taggedUsers: Set<string> = new Set();

  constructor() {
    // Initialize Drip's _dcq array if it doesn't exist
    if (typeof window !== 'undefined' && !window._dcq) {
      window._dcq = [];
    }
  }

  /**
   * Check if Drip JavaScript SDK is available
   */
  private isDripAvailable(): boolean {
    return typeof window !== 'undefined' && !!window._dcq;
  }

  /**
   * Apply a tag to a subscriber via Drip JavaScript SDK
   * @param email - User's email address
   * @param tag - Tag to apply (e.g., 'NoteGo_AI')
   * @param customFields - Optional custom fields to set
   */
  async applyTag(email: string, tag: string, customFields?: Record<string, any>): Promise<DripResponse> {
    if (!this.isDripAvailable()) {
      return { success: false, message: 'Drip SDK not available' };
    }

    if (!email || !tag) {
      return { success: false, message: 'Email and tag are required' };
    }

    try {
      // Use Drip's JavaScript SDK to identify and tag the user
      const identifyData: Record<string, unknown> = {
        email: email,
        tags: [tag],
      };

      // Add custom fields if provided
      if (customFields) {
        Object.assign(identifyData, customFields);
      }

      window._dcq.push(['identify', identifyData]);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Tag user as active NoteGo AI user
   * @param email - User's email address

   * @param planName - Optional plan name to include as custom field
   */
  async tagAsNoteGoUser(email: string, planName?: string): Promise<DripResponse> {
    // Prevent multiple tagging attempts for the same user in the same session
    const userKey = `${email}:NoteGo_AI`;
    if (this.taggedUsers.has(userKey)) {
      return { success: true, message: 'Already tagged in this session' };
    }

    // Include plan information if available
    const customFields = planName ? { Notego_plan: planName } : undefined;
    const result = await this.applyTag(email, 'NoteGo_AI', customFields);

    // Mark as tagged only if successful
    if (result.success) {
      this.taggedUsers.add(userKey);
    }

    return result;
  }

  /**

   * Update user's plan information in Drip
   * @param email - User's email address
   * @param planName - Current plan name (e.g., 'Free', 'Starter', 'Essentials', 'Pro')
   */
  async updatePlan(email: string, planName: string): Promise<DripResponse> {
    if (!this.isDripAvailable()) {
      return { success: false, message: 'Drip SDK not available' };
    }

    if (!email || !planName) {
      return { success: false, message: 'Email and plan name are required' };
    }

    try {
      // Update user's plan information without applying a tag
      window._dcq.push([
        'identify',
        {
          email: email,
          Notego_plan: planName,
        },
      ]);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Update the subscriber's device + browser in Drip as custom fields.
   * Mirrors the device/browser values recorded on the Firestore user doc
   * (see recordUserTechProfile in authStore) so Drip can segment on tech.
   * Fire-and-forget — never blocks auth.
   * @param email - User's email address
   * @param deviceType - Normalized device family (e.g., 'Windows', 'iPad', 'Android')
   * @param browser - Normalized browser family (e.g., 'Chrome', 'Safari', 'Edge')
   */
  async updateDeviceBrowser(email: string, deviceType: string, browser: string): Promise<DripResponse> {
    if (!this.isDripAvailable()) {
      return { success: false, message: 'Drip SDK not available' };
    }

    if (!email) {
      return { success: false, message: 'Email is required' };
    }

    try {
      window._dcq.push([
        'identify',
        {
          email: email,
          Notego_device_type: deviceType,
          Notego_browser: browser,
        },
      ]);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Clear the tagged users cache (useful for testing or when user logs out)
   */
  clearTaggedUsersCache(): void {
    this.taggedUsers.clear();
  }

  /**
   * Update plugin context data in Drip
   * Sends URL parameters from plugin redirects as custom fields
   * @param email - User's email address
   * @param data - Plugin context data from URL parameters
   */
  async updatePluginContext(email: string, data: PluginContextData): Promise<DripResponse> {
    if (!this.isDripAvailable()) {
      return { success: false, message: 'Drip SDK not available' };
    }

    if (!email) {
      return { success: false, message: 'Email is required' };
    }

    try {
      const identifyData: Record<string, any> = { email };

      // Map URL params to Drip custom field names (prefixed with Notego_ for consistency with API)
      if (data.videosTotal) identifyData.Notego_total_notes = data.videosTotal;
      if (data.onboardingStatus) identifyData.Notego_onboarding_status = data.onboardingStatus;
      if (data.micPermission) identifyData.Notego_mic_permission = data.micPermission;
      if (data.cameraPermission) identifyData.Notego_camera_permission = data.cameraPermission;
      if (data.isPinned) identifyData.Notego_is_pinned = data.isPinned;
      if (data.source) identifyData.Notego_source = data.source;

      window._dcq.push(['identify', identifyData]);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}

// Export singleton instance
export const dripService = new DripService();
export default dripService;
